import { useState, useEffect, useCallback } from "react";
import { useAuth, API, authFetch } from "@/App";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Settings, Users, Shield, Factory, Route, MapPin, ShoppingCart,
  Plus, Trash2, Edit, GripVertical, ChevronRight, Save,
  ToggleLeft, ToggleRight, DollarSign, Clock, CheckCircle,
  X, FileText, Layers, Eye, EyeOff, ArrowRight, ArrowLeftRight,
  Smartphone, Monitor, MessageSquare, Star, Inbox,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

// ── Roles Management Tab ──
const RolesTab = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRole, setEditRole] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newRole, setNewRole] = useState({ role_id: "", role_name: "", description: "" });

  const ALL_MODULES = [
    "dealers", "products", "orders", "collections", "schemes", "brochures",
    "employees", "targets", "tasks", "expenses", "attendance", "leave",
    "leaderboard", "approvals", "reports", "dealer-map", "sales-forecast",
    "chat", "webhooks", "grievances", "notifications", "users", "loyalty",
    "factory", "dispatch", "audit",
  ];
  const ALL_PERMS = ["create", "read", "update", "delete", "approve"];
  const FACTORY_SECTIONS = ["production", "raw_materials", "dispatch", "qc", "equipment", "safety", "energy", "reports", "material_entries", "daily_reports", "expenses", "manpower", "forecast", "tasks", "chat"];

  const fetchRoles = useCallback(async () => {
    const res = await authFetch(`${API}/admin/roles`);
    const data = await res.json();
    if (data.success) setRoles(data.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleCreateRole = async () => {
    if (!newRole.role_id) { toast.error("Role ID required"); return; }
    const res = await authFetch(`${API}/admin/roles`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...newRole, permissions: {}, allowed_modules: [], factory_sections: [] }),
    });
    const data = await res.json();
    if (data.success) { toast.success("Role created"); setShowCreate(false); setNewRole({ role_id: "", role_name: "", description: "" }); fetchRoles(); }
    else toast.error(data.detail || "Failed");
  };

  const handleSaveRole = async () => {
    if (!editRole) return;
    const res = await authFetch(`${API}/admin/roles/${editRole.role_id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editRole),
    });
    const data = await res.json();
    if (data.success) { toast.success("Role updated"); setEditRole(null); fetchRoles(); }
    else toast.error(data.detail || "Failed");
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm(`Delete role ${roleId}?`)) return;
    const res = await authFetch(`${API}/admin/roles/${roleId}`, { method: "DELETE" });
    const data = await res.json();
    if (data.success) { toast.success("Role deleted"); fetchRoles(); }
    else toast.error(data.detail || "Failed");
  };

  const toggleModule = (mod) => {
    if (!editRole) return;
    const mods = editRole.allowed_modules || [];
    setEditRole({ ...editRole, allowed_modules: mods.includes(mod) ? mods.filter(m => m !== mod) : [...mods, mod] });
  };

  const toggleFactorySection = (sec) => {
    if (!editRole) return;
    const secs = editRole.factory_sections || [];
    setEditRole({ ...editRole, factory_sections: secs.includes(sec) ? secs.filter(s => s !== sec) : [...secs, sec] });
  };

  if (loading) return <div className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="roles-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Manage roles and granular permissions</p>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="create-role-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Role
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {roles.map(role => (
          <Card key={role.role_id} className="p-4 dark:bg-slate-800" data-testid={`role-card-${role.role_id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-indigo-500" />
                  <span className="font-semibold text-sm dark:text-white">{role.role_name}</span>
                  {role.is_system && <Badge variant="outline" className="text-[10px]">System</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{role.description}</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setEditRole({ ...role })} data-testid={`edit-role-${role.role_id}`}>
                  <Edit className="w-3.5 h-3.5" />
                </Button>
                {!role.is_system && (
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteRole(role.role_id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(role.allowed_modules || []).slice(0, 5).map(m => (
                <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>
              ))}
              {(role.allowed_modules || []).length > 5 && <Badge variant="outline" className="text-[10px]">+{role.allowed_modules.length - 5}</Badge>}
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
              {role.can_approve && <span className="text-emerald-500">Can Approve</span>}
              {role.can_create_users && <span className="text-blue-500">Can Create Users</span>}
              {role.assigned_unit && <span>Unit: {role.assigned_unit}</span>}
            </div>
          </Card>
        ))}
      </div>

      {/* Create Role Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="create-role-dialog">
          <DialogHeader><DialogTitle>Create New Role</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Role ID (uppercase)</label>
              <Input value={newRole.role_id} onChange={e => setNewRole({ ...newRole, role_id: e.target.value.toUpperCase().replace(/\s/g, "_") })} placeholder="AREA_MANAGER" className="mt-1" data-testid="new-role-id" />
            </div>
            <div>
              <label className="text-sm font-medium">Display Name</label>
              <Input value={newRole.role_name} onChange={e => setNewRole({ ...newRole, role_name: e.target.value })} placeholder="Area Manager" className="mt-1" data-testid="new-role-name" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input value={newRole.description} onChange={e => setNewRole({ ...newRole, description: e.target.value })} placeholder="Manages area sales teams" className="mt-1" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreateRole} className="bg-[#8B1A1A] text-white" data-testid="confirm-create-role">Create</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editRole} onOpenChange={() => setEditRole(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-role-dialog">
          <DialogHeader><DialogTitle>Edit: {editRole?.role_name}</DialogTitle></DialogHeader>
          {editRole && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Display Name</label>
                  <Input value={editRole.role_name} onChange={e => setEditRole({ ...editRole, role_name: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Input value={editRole.description} onChange={e => setEditRole({ ...editRole, description: e.target.value })} className="mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Assigned Unit</label>
                  <Select value={editRole.assigned_unit || "none"} onValueChange={v => setEditRole({ ...editRole, assigned_unit: v === "none" ? null : v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">All Units</SelectItem>
                      <SelectItem value="Unit 1">Unit 1</SelectItem>
                      <SelectItem value="Unit 2">Unit 2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-4 pb-1">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={editRole.can_approve} onCheckedChange={v => setEditRole({ ...editRole, can_approve: v })} />
                    Can Approve
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={editRole.can_create_users} onCheckedChange={v => setEditRole({ ...editRole, can_create_users: v })} />
                    Can Create Users
                  </label>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Allowed Modules</label>
                <div className="flex flex-wrap gap-2">
                  {ALL_MODULES.map(mod => (
                    <button key={mod} onClick={() => toggleModule(mod)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition ${
                        (editRole.allowed_modules || []).includes(mod) || (editRole.allowed_modules || []).includes("*")
                          ? "bg-[#8B1A1A] text-white border-[#8B1A1A]" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                      }`} data-testid={`toggle-module-${mod}`}>
                      {mod}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Factory Sections</label>
                <div className="flex flex-wrap gap-2">
                  {FACTORY_SECTIONS.map(sec => (
                    <button key={sec} onClick={() => toggleFactorySection(sec)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition ${
                        (editRole.factory_sections || []).includes(sec) || (editRole.factory_sections || []).includes("*")
                          ? "bg-emerald-500 text-white border-emerald-500" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                      }`}>
                      {sec.replace(/_/g, " ")}
                    </button>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setEditRole(null)}>Cancel</Button>
                <Button onClick={handleSaveRole} className="bg-[#8B1A1A] text-white" data-testid="save-role-btn">
                  <Save className="w-3.5 h-3.5 mr-1" /> Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Form Builder Tab ──
const FormBuilderTab = () => {
  const [entityType, setEntityType] = useState("dealer");
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editField, setEditField] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newField, setNewField] = useState({ field_label: "", field_name: "", field_type: "text", required: false, section: "general", options: "", placeholder: "", visible_on_mobile: true, editable_by_employee: true });

  const fetchFields = useCallback(async () => {
    setLoading(true);
    const res = await authFetch(`${API}/admin/form-fields/${entityType}`);
    const data = await res.json();
    if (data.success) setFields(data.data);
    setLoading(false);
  }, [entityType]);

  useEffect(() => { fetchFields(); }, [fetchFields]);

  const handleCreate = async () => {
    const body = {
      ...newField,
      field_name: newField.field_name || newField.field_label.toLowerCase().replace(/\s+/g, "_"),
      options: newField.field_type === "dropdown" ? newField.options.split(",").map(o => o.trim()).filter(Boolean) : [],
    };
    const res = await authFetch(`${API}/admin/form-fields/${entityType}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.success) { toast.success("Field added"); setShowCreate(false); setNewField({ field_label: "", field_name: "", field_type: "text", required: false, section: "general", options: "", placeholder: "", visible_on_mobile: true, editable_by_employee: true }); fetchFields(); }
    else toast.error(data.detail || "Failed");
  };

  const handleUpdate = async () => {
    if (!editField) return;
    const body = { ...editField };
    if (typeof body.options === "string") body.options = body.options.split(",").map(o => o.trim()).filter(Boolean);
    const res = await authFetch(`${API}/admin/form-fields/${entityType}/${editField.field_id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.success) { toast.success("Field updated"); setEditField(null); fetchFields(); }
    else toast.error(data.detail || "Failed");
  };

  const handleDelete = async (fieldId) => {
    if (!confirm("Delete this field?")) return;
    const res = await authFetch(`${API}/admin/form-fields/${entityType}/${fieldId}`, { method: "DELETE" });
    const data = await res.json();
    if (data.success) { toast.success("Field removed"); fetchFields(); }
    else toast.error(data.detail || "Failed");
  };

  const FieldRow = ({ f }) => (
    <div className="flex items-center gap-3 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 dark:border-slate-700" data-testid={`field-${f.field_id}`}>
      <GripVertical className="w-4 h-4 text-slate-300 cursor-grab shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm dark:text-white truncate">{f.field_label}</span>
          <Badge variant="outline" className="text-[10px] shrink-0">{f.field_type}</Badge>
          {f.required && <Badge className="bg-red-100 text-red-600 text-[10px] shrink-0">Required</Badge>}
          {f.is_system && <Badge variant="outline" className="text-[10px] text-slate-400 shrink-0">System</Badge>}
        </div>
        <p className="text-[10px] text-slate-400 truncate">{f.field_name} &middot; Section: {f.section}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {f.visible_on_mobile ? <Eye className="w-3.5 h-3.5 text-emerald-500" title="Visible on mobile" /> : <EyeOff className="w-3.5 h-3.5 text-slate-300" title="Hidden on mobile" />}
        <Button size="sm" variant="ghost" onClick={() => setEditField({ ...f, options: Array.isArray(f.options) ? f.options.join(", ") : f.options })} data-testid={`edit-field-${f.field_id}`}>
          <Edit className="w-3.5 h-3.5" />
        </Button>
        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(f.field_id)} data-testid={`delete-field-${f.field_id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );

  const FieldFormFields = ({ data, setData }) => (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium">Field Label</label>
          <Input value={data.field_label} onChange={e => setData({ ...data, field_label: e.target.value })} placeholder="Annual Revenue" className="mt-1" data-testid="field-label-input" />
        </div>
        <div>
          <label className="text-sm font-medium">Field Name (key)</label>
          <Input value={data.field_name || ""} onChange={e => setData({ ...data, field_name: e.target.value })} placeholder="annual_revenue" className="mt-1" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-sm font-medium">Field Type</label>
          <Select value={data.field_type} onValueChange={v => setData({ ...data, field_type: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="dropdown">Dropdown</SelectItem>
              <SelectItem value="date">Date</SelectItem>
              <SelectItem value="file">File Upload</SelectItem>
              <SelectItem value="checkbox">Checkbox</SelectItem>
              <SelectItem value="textarea">Textarea</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium">Section</label>
          <Select value={data.section} onValueChange={v => setData({ ...data, section: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="business">Business Details</SelectItem>
              <SelectItem value="owner">Owner / Proprietor</SelectItem>
              <SelectItem value="bank">Bank Details</SelectItem>
              <SelectItem value="documents">Documents</SelectItem>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium">Placeholder</label>
          <Input value={data.placeholder || ""} onChange={e => setData({ ...data, placeholder: e.target.value })} className="mt-1" />
        </div>
      </div>
      {data.field_type === "dropdown" && (
        <div>
          <label className="text-sm font-medium">Options (comma separated)</label>
          <Input value={typeof data.options === "string" ? data.options : (data.options || []).join(", ")} onChange={e => setData({ ...data, options: e.target.value })} placeholder="Option 1, Option 2, Option 3" className="mt-1" data-testid="field-options-input" />
        </div>
      )}
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.required} onCheckedChange={v => setData({ ...data, required: v })} /> Required</label>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.visible_on_mobile} onCheckedChange={v => setData({ ...data, visible_on_mobile: v })} /> Visible on Mobile</label>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={data.editable_by_employee} onCheckedChange={v => setData({ ...data, editable_by_employee: v })} /> Editable by Employee</label>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="form-builder-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="w-40" data-testid="entity-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dealer">Dealer</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="order">Order</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-slate-500">{fields.length} fields configured</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="add-field-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Field
        </Button>
      </div>

      {loading ? <div className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></div> : (
        <div className="space-y-1.5">
          {fields.filter(f => f.active !== false).map(f => <FieldRow key={f.field_id} f={f} />)}
        </div>
      )}

      {/* Create Field Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg" data-testid="create-field-dialog">
          <DialogHeader><DialogTitle>Add Custom Field</DialogTitle></DialogHeader>
          <FieldFormFields data={newField} setData={setNewField} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#8B1A1A] text-white" data-testid="confirm-add-field">Add Field</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Field Dialog */}
      <Dialog open={!!editField} onOpenChange={() => setEditField(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Edit Field: {editField?.field_label}</DialogTitle></DialogHeader>
          {editField && <FieldFormFields data={editField} setData={setEditField} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditField(null)}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-[#8B1A1A] text-white" data-testid="save-field-btn"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Order Workflow Tab ──
const OrderWorkflowTab = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await authFetch(`${API}/admin/order-workflow`);
      const data = await res.json();
      if (data.success) setConfig(data.data);
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const res = await authFetch(`${API}/admin/order-workflow`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(config),
    });
    const data = await res.json();
    if (data.success) toast.success("Workflow saved");
    else toast.error("Failed to save");
    setSaving(false);
  };

  if (loading || !config) return <div className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="order-workflow-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 dark:bg-slate-800">
          <h3 className="font-semibold text-sm mb-3 dark:text-white">Approval Settings</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={config.require_admin_approval} onCheckedChange={v => setConfig({ ...config, require_admin_approval: v })} />
              Require Admin Approval for all orders
            </label>
            <div>
              <label className="text-sm text-slate-600">Auto-approve below amount (0 = disabled)</label>
              <Input type="number" value={config.auto_approve_below_amount || 0} onChange={e => setConfig({ ...config, auto_approve_below_amount: parseInt(e.target.value) || 0 })} className="mt-1" data-testid="auto-approve-amount" />
            </div>
            <div>
              <label className="text-sm text-slate-600">Edit window (hours after placement)</label>
              <Input type="number" value={config.edit_window_hours || 1} onChange={e => setConfig({ ...config, edit_window_hours: parseInt(e.target.value) || 1 })} className="mt-1" />
            </div>
          </div>
        </Card>

        <Card className="p-4 dark:bg-slate-800">
          <h3 className="font-semibold text-sm mb-3 dark:text-white">Unit Routing</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={config.allow_salesman_unit_selection} onCheckedChange={v => setConfig({ ...config, allow_salesman_unit_selection: v })} />
              Allow salesman to select target unit
            </label>
            <div>
              <label className="text-sm text-slate-600">Default Unit</label>
              <Select value={config.default_unit || "unit_1"} onValueChange={v => setConfig({ ...config, default_unit: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unit_1">Unit 1</SelectItem>
                  <SelectItem value="unit_2">Unit 2</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={config.notify_on_status_change} onCheckedChange={v => setConfig({ ...config, notify_on_status_change: v })} />
              Notify on status change (WebSocket)
            </label>
          </div>
        </Card>
      </div>

      <Card className="p-4 dark:bg-slate-800">
        <h3 className="font-semibold text-sm mb-3 dark:text-white">Order Statuses</h3>
        <div className="flex flex-wrap gap-2">
          {(config.statuses || []).map((s, i) => (
            <div key={i} className="flex items-center gap-1">
              <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">{s}</Badge>
              {i < (config.statuses || []).length - 1 && <ChevronRight className="w-3 h-3 text-slate-400" />}
            </div>
          ))}
        </div>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="save-workflow-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Workflow Configuration"}
      </Button>
    </div>
  );
};

// ── Factory Units Tab ──
const FactoryUnitsTab = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUnit, setEditUnit] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await authFetch(`${API}/admin/factory-units`);
      const data = await res.json();
      if (data.success) setUnits(data.data);
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    if (!editUnit) return;
    const res = await authFetch(`${API}/admin/factory-units/${editUnit.unit_id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(editUnit),
    });
    const data = await res.json();
    if (data.success) {
      toast.success("Unit updated");
      setEditUnit(null);
      const res2 = await authFetch(`${API}/admin/factory-units`);
      const data2 = await res2.json();
      if (data2.success) setUnits(data2.data);
    }
    else toast.error("Failed");
  };

  if (loading) return <div className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="factory-units-tab">
      <p className="text-sm text-slate-500">Configure factory units, supervisors, and product assignments</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {units.map(u => (
          <Card key={u.unit_id} className="p-4 dark:bg-slate-800 border-l-4" style={{ borderLeftColor: u.color || "#0EA5E9" }} data-testid={`unit-card-${u.unit_id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-bold text-lg dark:text-white">{u.unit_name}</h3>
                <p className="text-xs text-slate-500">{u.location}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditUnit({ ...u })} data-testid={`edit-unit-${u.unit_id}`}>
                <Edit className="w-3.5 h-3.5 mr-1" /> Configure
              </Button>
            </div>
            <div className="text-xs text-slate-500 space-y-1">
              <p>Supervisors: {(u.supervisor_user_ids || []).length || "Not assigned"}</p>
              <p>Products: {(u.products || []).length || "All"}</p>
              <p>Status: {u.active ? <span className="text-emerald-600">Active</span> : <span className="text-red-600">Inactive</span>}</p>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!editUnit} onOpenChange={() => setEditUnit(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Configure {editUnit?.unit_name}</DialogTitle></DialogHeader>
          {editUnit && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Unit Name</label>
                <Input value={editUnit.unit_name} onChange={e => setEditUnit({ ...editUnit, unit_name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium">Location</label>
                <Input value={editUnit.location || ""} onChange={e => setEditUnit({ ...editUnit, location: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium">Color (hex)</label>
                <Input value={editUnit.color || ""} onChange={e => setEditUnit({ ...editUnit, color: e.target.value })} className="mt-1" placeholder="#0EA5E9" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={editUnit.active} onCheckedChange={v => setEditUnit({ ...editUnit, active: v })} />
                Active
              </label>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditUnit(null)}>Cancel</Button>
                <Button onClick={handleSave} className="bg-[#8B1A1A] text-white"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── TADA Configuration Tab ──
const TADAConfigTab = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await authFetch(`${API}/admin/tada-config`);
      const data = await res.json();
      if (data.success) setConfig(data.data);
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const res = await authFetch(`${API}/admin/tada-config`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(config),
    });
    const data = await res.json();
    if (data.success) toast.success("TADA config saved");
    else toast.error("Failed");
    setSaving(false);
  };

  if (loading || !config) return <div className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="tada-config-tab">
      <p className="text-sm text-slate-500">Configure travel allowance and daily allowance calculations</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 dark:bg-slate-800">
          <h3 className="font-semibold text-sm mb-3 dark:text-white flex items-center gap-2"><Route className="w-4 h-4" /> Travel Allowance</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-slate-600">Rate per KM (Rs)</label>
              <Input type="number" step="0.5" value={config.rate_per_km} onChange={e => setConfig({ ...config, rate_per_km: parseFloat(e.target.value) || 0 })} className="mt-1" data-testid="tada-rate-km" />
            </div>
          </div>
        </Card>
        <Card className="p-4 dark:bg-slate-800">
          <h3 className="font-semibold text-sm mb-3 dark:text-white flex items-center gap-2"><DollarSign className="w-4 h-4" /> Daily Allowance</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-slate-600">Daily Allowance (Rs)</label>
              <Input type="number" value={config.daily_allowance} onChange={e => setConfig({ ...config, daily_allowance: parseInt(e.target.value) || 0 })} className="mt-1" />
            </div>
            <div>
              <label className="text-sm text-slate-600">Food Allowance (Rs)</label>
              <Input type="number" value={config.food_allowance} onChange={e => setConfig({ ...config, food_allowance: parseInt(e.target.value) || 0 })} className="mt-1" />
            </div>
          </div>
        </Card>
      </div>
      <Card className="p-4 dark:bg-slate-800">
        <h3 className="font-semibold text-sm mb-3 dark:text-white flex items-center gap-2"><Clock className="w-4 h-4" /> Hour Thresholds</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-slate-600">Min hours for full TADA</label>
            <Input type="number" value={config.min_hours_for_full_tada} onChange={e => setConfig({ ...config, min_hours_for_full_tada: parseInt(e.target.value) || 0 })} className="mt-1" />
          </div>
          <div>
            <label className="text-sm text-slate-600">Min hours for half TADA</label>
            <Input type="number" value={config.min_hours_for_half_tada} onChange={e => setConfig({ ...config, min_hours_for_half_tada: parseInt(e.target.value) || 0 })} className="mt-1" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm mt-3">
          <Checkbox checked={config.auto_calculate} onCheckedChange={v => setConfig({ ...config, auto_calculate: v })} />
          Auto-calculate TADA from GPS data
        </label>
      </Card>
      <Button onClick={handleSave} disabled={saving} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="save-tada-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save TADA Configuration"}
      </Button>
    </div>
  );
};

// ── Information Flow Tab ──
const InformationFlowTab = () => {
  const [flowConfig, setFlowConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await authFetch(`${API}/admin/config/info_flow`);
      const data = await res.json();
      if (data.success && data.data) setFlowConfig(data.data);
      else setFlowConfig(getDefaultFlowConfig());
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    await authFetch(`${API}/admin/config/info_flow`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ value: flowConfig }),
    });
    toast.success("Information flow config saved");
    setSaving(false);
  };

  const toggleFlow = (key) => {
    setFlowConfig(prev => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }));
  };

  const FLOWS = [
    { key: "orders", label: "Orders", admin: "Approve / Reject / Hold / Route to Unit", mobile: "Place Order / View Status / Receive Notifications", icon: ShoppingCart, color: "text-[#8B1A1A]" },
    { key: "dealers", label: "Dealers", admin: "Approve / Reject / Edit / Assign Category", mobile: "Register New Dealer / View Status / Receive Suggestions", icon: Users, color: "text-blue-500" },
    { key: "attendance", label: "Attendance", admin: "View Dashboard / Download Reports", mobile: "Punch In/Out / GPS Ping / Battery Status", icon: Clock, color: "text-emerald-500" },
    { key: "targets", label: "Targets", admin: "Assign Targets / Track Achievement", mobile: "View My Targets / Achievement Progress", icon: ChevronRight, color: "text-purple-500" },
    { key: "tasks", label: "Tasks", admin: "Create & Assign Tasks / Track Status", mobile: "View Tasks / Update Status / Add Notes", icon: FileText, color: "text-amber-500" },
    { key: "expenses", label: "Expenses", admin: "Approve / Reject / Hold Expenses", mobile: "Submit Expense / Upload Receipt / View Status", icon: DollarSign, color: "text-rose-500" },
    { key: "leaves", label: "Leaves", admin: "Approve / Reject / Hold Leave Requests", mobile: "Apply Leave / View Leave Balance", icon: Clock, color: "text-sky-500" },
    { key: "schemes", label: "Schemes", admin: "Create Scheme / Assign to Dealers", mobile: "View Active Schemes / Share with Dealers", icon: Star, color: "text-indigo-500" },
    { key: "collections", label: "Collections", admin: "Track Collection / Approve Payments", mobile: "Record Collection / Upload Proof", icon: DollarSign, color: "text-teal-500" },
    { key: "brochures", label: "Brochures", admin: "Upload / Edit / Delete Brochures", mobile: "Download / Share Brochures (Read-Only)", icon: FileText, color: "text-orange-500" },
    { key: "grievances", label: "Grievances", admin: "Assign / Resolve / Close Grievances", mobile: "Submit Grievance / Track Status", icon: MessageSquare, color: "text-red-500" },
    { key: "notifications", label: "Notifications", admin: "Trigger via approval actions (WebSocket)", mobile: "Receive real-time via WebSocket + polling", icon: Inbox, color: "text-violet-500" },
  ];

  if (loading) return <div className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="info-flow-tab">
      <p className="text-sm text-slate-500 dark:text-slate-400">Configure data flow between Admin Panel and Mobile App. Toggle modules and manage sync direction.</p>

      {/* Flow Legend */}
      <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
        <span className="flex items-center gap-1"><Monitor className="w-3 h-3" /> Admin Panel</span>
        <ArrowLeftRight className="w-3.5 h-3.5 text-[#8B1A1A]" />
        <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" /> Mobile App</span>
      </div>

      <div className="space-y-2">
        {FLOWS.map(flow => {
          const cfg = flowConfig?.[flow.key] || { enabled: true };
          return (
            <Card key={flow.key} className={`p-4 dark:bg-slate-800 transition ${!cfg.enabled ? "opacity-50" : ""}`} data-testid={`flow-${flow.key}`}>
              <div className="flex items-center gap-4">
                {/* Toggle */}
                <button onClick={() => toggleFlow(flow.key)} className="shrink-0" data-testid={`toggle-flow-${flow.key}`}>
                  {cfg.enabled
                    ? <ToggleRight className="w-8 h-5 text-emerald-500" />
                    : <ToggleLeft className="w-8 h-5 text-slate-400" />}
                </button>

                {/* Icon */}
                <flow.icon className={`w-5 h-5 ${flow.color} shrink-0`} />

                {/* Flow Details */}
                <div className="flex-1 grid grid-cols-3 gap-2 items-center min-w-0">
                  <div>
                    <p className="font-semibold text-sm text-slate-900 dark:text-white">{flow.label}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 text-right">
                      <p className="text-[10px] text-slate-400">Admin Panel</p>
                      <p className="text-xs text-slate-600 dark:text-slate-300 truncate">{flow.admin}</p>
                    </div>
                    <ArrowLeftRight className="w-4 h-4 text-[#8B1A1A] shrink-0" />
                    <div className="flex-1">
                      <p className="text-[10px] text-slate-400">Mobile App</p>
                      <p className="text-xs text-slate-600 dark:text-slate-300 truncate">{flow.mobile}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={cfg.enabled ? "bg-emerald-100 text-emerald-700 text-[10px]" : "bg-slate-100 text-slate-500 text-[10px]"}>
                      {cfg.enabled ? "Active" : "Disabled"}
                    </Badge>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="save-flow-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Flow Configuration"}
      </Button>
    </div>
  );
};

const getDefaultFlowConfig = () => {
  const keys = ["orders", "dealers", "attendance", "targets", "tasks", "expenses", "leaves", "schemes", "collections", "brochures", "grievances", "notifications"];
  const cfg = {};
  keys.forEach(k => { cfg[k] = { enabled: true }; });
  return cfg;
};

// ── Forms Designer Tab (Feedback & Suggestions) ──
const FormsDesignerTab = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editForm, setEditForm] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newForm, setNewForm] = useState({
    form_name: "", form_type: "FEEDBACK", description: "", fields: [],
    target_audience: "ALL", active: true,
  });
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldType, setNewFieldType] = useState("text");

  useEffect(() => {
    (async () => {
      const res = await authFetch(`${API}/admin/survey-forms`);
      const data = await res.json();
      if (data.success) setForms(data.data);
      setLoading(false);
    })();
  }, []);

  const handleCreate = async () => {
    if (!newForm.form_name) { toast.error("Form name required"); return; }
    const res = await authFetch(`${API}/admin/survey-forms`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newForm),
    });
    const data = await res.json();
    if (data.success) {
      toast.success("Form created");
      setShowCreate(false);
      setNewForm({ form_name: "", form_type: "FEEDBACK", description: "", fields: [], target_audience: "ALL", active: true });
      const res2 = await authFetch(`${API}/admin/survey-forms`);
      const data2 = await res2.json();
      if (data2.success) setForms(data2.data);
    }
  };

  const handleUpdate = async () => {
    if (!editForm) return;
    const res = await authFetch(`${API}/admin/survey-forms/${editForm.form_id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(editForm),
    });
    const data = await res.json();
    if (data.success) {
      toast.success("Form updated");
      setEditForm(null);
      const res2 = await authFetch(`${API}/admin/survey-forms`);
      const data2 = await res2.json();
      if (data2.success) setForms(data2.data);
    }
  };

  const handleDelete = async (formId) => {
    if (!confirm("Delete this form?")) return;
    await authFetch(`${API}/admin/survey-forms/${formId}`, { method: "DELETE" });
    toast.success("Form deleted");
    setForms(forms.filter(f => f.form_id !== formId));
  };

  const addField = (target, setTarget) => {
    if (!newFieldLabel) return;
    const field = {
      id: `fld_${Date.now()}`,
      label: newFieldLabel,
      type: newFieldType,
      required: false,
      options: [],
    };
    setTarget({ ...target, fields: [...(target.fields || []), field] });
    setNewFieldLabel("");
  };

  const removeField = (target, setTarget, fieldId) => {
    setTarget({ ...target, fields: (target.fields || []).filter(f => f.id !== fieldId) });
  };

  const FieldDesigner = ({ data, setData }) => (
    <div className="space-y-3">
      <div className="space-y-1.5">
        {(data.fields || []).map((f, i) => (
          <div key={f.id} className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 dark:bg-slate-900 rounded-lg">
            <GripVertical className="w-3.5 h-3.5 text-slate-300 shrink-0 cursor-grab" />
            <span className="text-sm flex-1 text-slate-700 dark:text-slate-300">{f.label}</span>
            <Badge variant="outline" className="text-[10px] shrink-0">{f.type}</Badge>
            <label className="flex items-center gap-1 text-[10px] text-slate-500">
              <Checkbox checked={f.required} onCheckedChange={v => {
                const fields = [...data.fields];
                fields[i] = { ...fields[i], required: v };
                setData({ ...data, fields });
              }} />
              Req
            </label>
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removeField(data, setData, f.id)}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)} placeholder="Field label" className="flex-1" onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addField(data, setData))} data-testid="new-survey-field-label" />
        <Select value={newFieldType} onValueChange={setNewFieldType}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="text">Text</SelectItem>
            <SelectItem value="textarea">Textarea</SelectItem>
            <SelectItem value="rating">Rating (1-5)</SelectItem>
            <SelectItem value="dropdown">Dropdown</SelectItem>
            <SelectItem value="checkbox">Checkbox</SelectItem>
            <SelectItem value="number">Number</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={() => addField(data, setData)} data-testid="add-survey-field">
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );

  if (loading) return <div className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="forms-designer-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 dark:text-slate-400">Design feedback forms, surveys & suggestion forms for employees and dealers</p>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="create-survey-form-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Form
        </Button>
      </div>

      {forms.length === 0 ? (
        <Card className="p-8 text-center dark:bg-slate-800">
          <MessageSquare className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 dark:text-slate-400">No forms created yet</p>
          <p className="text-xs text-slate-400 mt-1">Create feedback, survey, or suggestion forms for employees and dealers</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {forms.map(form => (
            <Card key={form.form_id} className="p-4 dark:bg-slate-800" data-testid={`survey-form-${form.form_id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#8B1A1A]" />
                    <span className="font-semibold text-sm dark:text-white">{form.form_name}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{form.description || "No description"}</p>
                </div>
                <div className="flex gap-1">
                  <Badge className={`text-[10px] ${form.form_type === "FEEDBACK" ? "bg-blue-100 text-blue-700" : form.form_type === "SURVEY" ? "bg-purple-100 text-purple-700" : "bg-amber-100 text-amber-700"}`}>{form.form_type}</Badge>
                  <Badge className={`text-[10px] ${form.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{form.active ? "Active" : "Inactive"}</Badge>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mb-2">{(form.fields || []).length} fields · Audience: {form.target_audience}</p>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setEditForm({ ...form })} data-testid={`edit-survey-${form.form_id}`}>
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(form.form_id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Form Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-survey-dialog">
          <DialogHeader><DialogTitle>Create Form</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={newForm.form_name} onChange={e => setNewForm({ ...newForm, form_name: e.target.value })} placeholder="Form name" data-testid="survey-form-name" />
            <Input value={newForm.description} onChange={e => setNewForm({ ...newForm, description: e.target.value })} placeholder="Description" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={newForm.form_type} onValueChange={v => setNewForm({ ...newForm, form_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FEEDBACK">Feedback</SelectItem>
                  <SelectItem value="SURVEY">Survey</SelectItem>
                  <SelectItem value="SUGGESTION">Suggestion</SelectItem>
                </SelectContent>
              </Select>
              <Select value={newForm.target_audience} onValueChange={v => setNewForm({ ...newForm, target_audience: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Users</SelectItem>
                  <SelectItem value="EMPLOYEES">Employees Only</SelectItem>
                  <SelectItem value="DEALERS">Dealers Only</SelectItem>
                  <SelectItem value="SALESMEN">Salesmen Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t pt-3">
              <h4 className="text-sm font-medium mb-2 dark:text-white">Form Fields</h4>
              <FieldDesigner data={newForm} setData={setNewForm} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-[#8B1A1A] text-white" data-testid="confirm-create-survey">Create Form</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Form Dialog */}
      <Dialog open={!!editForm} onOpenChange={() => setEditForm(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit: {editForm?.form_name}</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <Input value={editForm.form_name} onChange={e => setEditForm({ ...editForm, form_name: e.target.value })} />
              <Input value={editForm.description || ""} onChange={e => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={editForm.form_type} onValueChange={v => setEditForm({ ...editForm, form_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FEEDBACK">Feedback</SelectItem>
                    <SelectItem value="SURVEY">Survey</SelectItem>
                    <SelectItem value="SUGGESTION">Suggestion</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={editForm.target_audience} onValueChange={v => setEditForm({ ...editForm, target_audience: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Users</SelectItem>
                    <SelectItem value="EMPLOYEES">Employees Only</SelectItem>
                    <SelectItem value="DEALERS">Dealers Only</SelectItem>
                    <SelectItem value="SALESMEN">Salesmen Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={editForm.active} onCheckedChange={v => setEditForm({ ...editForm, active: v })} />
                Active
              </label>
              <div className="border-t pt-3">
                <h4 className="text-sm font-medium mb-2 dark:text-white">Form Fields</h4>
                <FieldDesigner data={editForm} setData={setEditForm} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditForm(null)}>Cancel</Button>
                <Button onClick={handleUpdate} className="bg-[#8B1A1A] text-white"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Main Admin Configuration Page ──
const AdminConfigPage = () => {
  const { user } = useAuth();
  if (user?.role !== "SUPER_ADMIN") return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="admin-config-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Sora']">System Configuration</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Configure roles, permissions, form fields, workflows, factory units, and TADA</p>
      </div>

      <Tabs defaultValue="roles">
        <TabsList className="bg-slate-100 dark:bg-slate-800 flex-wrap">
          <TabsTrigger value="roles" className="gap-1.5" data-testid="config-tab-roles"><Shield className="w-3.5 h-3.5" /> Roles & Permissions</TabsTrigger>
          <TabsTrigger value="forms" className="gap-1.5" data-testid="config-tab-forms"><Layers className="w-3.5 h-3.5" /> Form Builder</TabsTrigger>
          <TabsTrigger value="orders" className="gap-1.5" data-testid="config-tab-orders"><ShoppingCart className="w-3.5 h-3.5" /> Order Workflow</TabsTrigger>
          <TabsTrigger value="factory" className="gap-1.5" data-testid="config-tab-factory"><Factory className="w-3.5 h-3.5" /> Factory Units</TabsTrigger>
          <TabsTrigger value="tada" className="gap-1.5" data-testid="config-tab-tada"><Route className="w-3.5 h-3.5" /> TADA Config</TabsTrigger>
          <TabsTrigger value="flow" className="gap-1.5" data-testid="config-tab-flow"><ArrowLeftRight className="w-3.5 h-3.5" /> Info Flow</TabsTrigger>
          <TabsTrigger value="surveys" className="gap-1.5" data-testid="config-tab-surveys"><MessageSquare className="w-3.5 h-3.5" /> Forms Designer</TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="mt-4"><RolesTab /></TabsContent>
        <TabsContent value="forms" className="mt-4"><FormBuilderTab /></TabsContent>
        <TabsContent value="orders" className="mt-4"><OrderWorkflowTab /></TabsContent>
        <TabsContent value="factory" className="mt-4"><FactoryUnitsTab /></TabsContent>
        <TabsContent value="tada" className="mt-4"><TADAConfigTab /></TabsContent>
        <TabsContent value="flow" className="mt-4"><InformationFlowTab /></TabsContent>
        <TabsContent value="surveys" className="mt-4"><FormsDesignerTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminConfigPage;
